// hooks/useScrollMenuHandler.ts
import { useEffect, useRef, useState } from 'react';
import menuStore from '../store/MenuStore';

export const useHideMenu = () => {
  const SCROLL_THRESHOLD = 30;
  const SCROLL_COOLDOWN_MS = 1000;
  const INACTIVITY_MS = 3000; // ✅ 스크롤 없을 때 숨길 대기 시간(3초)

  const [lastScrollTime, setLastScrollTime] = useState<number>(0);
  const prevScrollYRef = useRef(0);
  const inactivityTimerRef = useRef<number | null>(null);

  // ✅ 비활성 타이머 초기화 & 재시작
  const restartInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    // 최상단이면 숨기지 않음
    if (window.scrollY < 50) return;

    inactivityTimerRef.current = window.setTimeout(() => {
      // 여전히 최상단이 아니라면 숨김
      if (window.scrollY > 0 && !menuStore.isOpen) {
        menuStore.hide();
      }
    }, INACTIVITY_MS);
  };

  useEffect(() => {
    const onScroll = () => {
      const now = Date.now();
      let isSnapDown = false;
      let isSnapUp = false;
      let scrollDown = false;
      let scrollUp = false;

      const deltaY = window.scrollY - prevScrollYRef.current;

      if (now - lastScrollTime >= SCROLL_COOLDOWN_MS) {
        const absDeltaY = Math.abs(deltaY);
        if (absDeltaY >= SCROLL_THRESHOLD) {
          isSnapDown = deltaY > 0;
          isSnapUp = deltaY < 0;
          setLastScrollTime(now);
        } else if (absDeltaY >= 10) {
          scrollDown = deltaY > 0;
          scrollUp = deltaY < 0;
        }
      }

      prevScrollYRef.current = window.scrollY;

      // ✅ 스크롤이 발생했으므로 비활성 타이머 재시작
      restartInactivityTimer();
    };

    // 첫 진입 시 상태 보장: 최상단이면 메뉴 표시
    if (window.scrollY === 0) {
      menuStore.show();
    } else {
      // 상단이 아니면 비활성 타이머 시작
      restartInactivityTimer();
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    // 탭 전환 등으로 스크롤 멈춤과 비슷한 상황에서 과도하게 숨지 않도록 처리(선택)
    const onVisibility = () => {
      if (document.hidden && inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      } else {
        restartInactivityTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [lastScrollTime]);
};
